import {
  Box,
  FormControl,
  Select,
  MenuItem,
  Checkbox,
  ListItemText,
  Collapse,
  IconButton,
} from "@mui/material";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import { JobStatus, useGetJobCitiesQuery } from "generated/graphql";
import { capitalCase } from "change-case";
import { enumToValueOptions } from "utils/enums/enumToValueOptions";
import OrganizationProductTypeIdsOptions from "modules/OrganizationProductTypes/OrganizationProductTypeIdsOptions/OrganizationProductTypeIdsOptions";

export type SortOption =
  | "WON_DATE_DESC"
  | "WON_DATE_ASC"
  | "CITY_ASC"
  | "CITY_DESC"
  | "TOTAL_ASC"
  | "TOTAL_DESC";

export type SortField = "WON_DATE" | "CITY" | "TOTAL";
export type SortDirection = "ASC" | "DESC";

const sortFieldOptions: { value: SortField; label: string }[] = [
  { value: "WON_DATE", label: "Won Date" },
  { value: "CITY", label: "City" },
  { value: "TOTAL", label: "Job Total" },
];

const getSortField = (sortOption: SortOption): SortField => {
  if (sortOption.startsWith("WON_DATE")) return "WON_DATE";
  if (sortOption.startsWith("CITY")) return "CITY";
  return "TOTAL";
};

const getSortDirection = (sortOption: SortOption): SortDirection => {
  return sortOption.endsWith("_ASC") ? "ASC" : "DESC";
};

const buildSortOption = (field: SortField, direction: SortDirection): SortOption => {
  return `${field}_${direction}` as SortOption;
};

interface FiltersDrawerProps {
  open: boolean;
  statuses: JobStatus[];
  onStatusChange: (statuses: JobStatus[]) => void;
  cities: string[];
  onCitiesChange: (cities: string[]) => void;
  organizationProductTypeIds: any[];
  onProductTypeChange: (productTypes: any[]) => void;
  sortBy: SortOption;
  onSortChange: (sort: SortOption) => void;
}

const selectStyles = {
  height: "48px",
  padding: 0,
  borderRadius: "8px",
  fontSize: "16px",
  backgroundColor: "white!important",
  "& .MuiSelect-select": {
    padding: "16px",
    borderRadius: "8px",
  },
  "& .MuiOutlinedInput-notchedOutline": {
    borderColor: "grey.300",
    borderRadius: "8px",
  },
  "&:hover .MuiOutlinedInput-notchedOutline": {
    borderColor: "grey.400",
  },
  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
    borderColor: "primary.main",
    borderWidth: "1px",
  },
  "& .MuiSelect-icon": {
    color: "grey.600",
    fontSize: "24px",
  },
};

export default function FiltersDrawer({
  open,
  statuses,
  onStatusChange,
  cities,
  onCitiesChange,
  organizationProductTypeIds,
  onProductTypeChange,
  sortBy,
  onSortChange,
}: FiltersDrawerProps) {
  const { data: citiesData, loading: citiesLoading } = useGetJobCitiesQuery();
  const availableCities = citiesData?.getJobCities || [];

  const currentField = getSortField(sortBy);
  const currentDirection = getSortDirection(sortBy);

  const handleFieldChange = (newField: SortField) => {
    onSortChange(buildSortOption(newField, currentDirection));
  };

  const handleDirectionToggle = () => {
    const newDirection = currentDirection === "ASC" ? "DESC" : "ASC";
    onSortChange(buildSortOption(currentField, newDirection));
  };

  return (
    <Collapse in={open} timeout="auto" unmountOnExit>
      <Box sx={{ mb: 2 }}>
        <Box mb={2} display="flex" gap={1}>
          <FormControl sx={{ flex: 1 }}>
            <Select
              value={currentField}
              onChange={(e) => handleFieldChange(e.target.value as SortField)}
              displayEmpty
              renderValue={(selected) => {
                const option = sortFieldOptions.find((opt) => opt.value === selected);
                return option ? `Sort: ${option.label}` : "Sort By";
              }}
              sx={selectStyles}
            >
              {sortFieldOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <IconButton
            onClick={handleDirectionToggle}
            sx={{
              border: "1px solid",
              borderColor: "grey.300",
              borderRadius: "8px",
              width: "48px",
              height: "48px",
              backgroundColor: "white",
              "&:hover": {
                backgroundColor: "grey.50",
              },
            }}
            title={currentDirection === "ASC" ? "Ascending" : "Descending"}
          >
            {currentDirection === "ASC" ? <ArrowUpwardIcon /> : <ArrowDownwardIcon />}
          </IconButton>
        </Box>

        <Box mb={2}>
          <FormControl fullWidth>
            <Select
              multiple
              value={statuses}
              onChange={(e) => onStatusChange(e.target.value as JobStatus[])}
              displayEmpty
              renderValue={(selected) => {
                if (selected.length === 0) {
                  return <span style={{ color: "#9e9e9e" }}>Status</span>;
                }
                return selected.map((status) => capitalCase(status)).join(", ");
              }}
              sx={selectStyles}
            >
              {enumToValueOptions(JobStatus).map((status) => (
                <MenuItem key={status.value} value={status.value}>
                  <Checkbox checked={statuses.includes(status.value as JobStatus)} />
                  <ListItemText primary={capitalCase(status.label)} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        <Box mb={2}>
          <OrganizationProductTypeIdsOptions>
            {({ organizationProductTypeIdsOptions }) => (
              <FormControl fullWidth>
                <Select
                  multiple
                  value={organizationProductTypeIds.map((typeId) => typeId.value)}
                  onChange={(e) => {
                    const result = organizationProductTypeIdsOptions.filter((option) =>
                      (e.target.value as string[]).includes(`${option.value}`)
                    );
                    onProductTypeChange(result);
                  }}
                  displayEmpty
                  renderValue={(selected) => {
                    if (selected.length === 0) {
                      return <span style={{ color: "#9e9e9e" }}>Product Type</span>;
                    }
                    return organizationProductTypeIds.map((typeId) => typeId.label).join(", ");
                  }}
                  sx={selectStyles}
                >
                  {organizationProductTypeIdsOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      <Checkbox
                        checked={organizationProductTypeIds.some(
                          (typeId) => typeId.value === option.value
                        )}
                      />
                      <ListItemText primary={option.label} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
          </OrganizationProductTypeIdsOptions>
        </Box>

        <Box mb={2}>
          <FormControl fullWidth>
            <Select
              multiple
              value={cities}
              onChange={(e) => onCitiesChange(e.target.value as string[])}
              displayEmpty
              disabled={citiesLoading}
              renderValue={(selected) => {
                if (selected.length === 0) {
                  return <span style={{ color: "#9e9e9e" }}>City</span>;
                }
                return selected.join(", ");
              }}
              sx={selectStyles}
            >
              {availableCities.map((city) => (
                <MenuItem key={city} value={city}>
                  <Checkbox checked={cities.includes(city)} />
                  <ListItemText primary={city} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      </Box>
    </Collapse>
  );
}
